/**
 * Books slice — multi-book management with active-book selection.
 *
 * Active book resolution order on `setItems` (when current activeBookId is
 * null or refers to a missing/deleted book):
 *   1. localStorage `mochia.book` (if it points to a present book)
 *   2. The book with `is_default=true`
 *   3. The first book in the list
 *
 * Sharp edge: removing the last remaining book throws
 * `LastBookUndeletableError`. UI must filter delete affordances accordingly,
 * but the slice enforces the invariant defensively.
 */

import type { BookRow } from '@/lib/sheets/types';
import type { MochiaError } from '@/lib/errors';

export const ACTIVE_BOOK_LS_KEY = 'mochia.book';

export class LastBookUndeletableError extends Error implements MochiaError {
  readonly hint: string;
  constructor() {
    super('Cannot delete the last remaining book');
    this.name = 'LastBookUndeletableError';
    this.hint = '請先建立另一本帳本並切換過去，再刪除這本。';
  }
}

export interface BooksState {
  items: BookRow[];
  activeBookId: string | null;
}

export interface BooksActions {
  setItems: (items: BookRow[]) => void;
  setActiveBookId: (id: string | null) => void;
  addBook: (book: BookRow) => void;
  updateBook: (id: string, patch: Partial<BookRow>) => void;
  removeBook: (id: string) => void;
}

export type BooksSlice = BooksState & BooksActions;

export const initialBooksState: BooksState = {
  items: [],
  activeBookId: null,
};

function readLocalActiveBookId(): string | null {
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(ACTIVE_BOOK_LS_KEY)
      : null;
  } catch {
    return null;
  }
}

function writeLocalActiveBookId(id: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (id == null) localStorage.removeItem(ACTIVE_BOOK_LS_KEY);
    else localStorage.setItem(ACTIVE_BOOK_LS_KEY, id);
  } catch {
    // ignore storage errors
  }
}

function resolveActiveBookId(items: BookRow[], current: string | null): string | null {
  const present = (id: string | null): boolean =>
    !!id && items.some((b) => b.id === id && !b.deleted_at);

  if (present(current)) return current;

  const fromLs = readLocalActiveBookId();
  if (present(fromLs)) return fromLs;

  const def = items.find((b) => b.is_default && !b.deleted_at);
  if (def) return def.id;

  const first = items.find((b) => !b.deleted_at);
  return first ? first.id : null;
}

export const createBooksSlice =
  <T extends BooksSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): BooksSlice => ({
    ...initialBooksState,

    setItems: (items) =>
      set((state) => {
        const activeBookId = resolveActiveBookId(items, state.activeBookId);
        return { items, activeBookId } as Partial<T>;
      }),

    setActiveBookId: (id) => {
      writeLocalActiveBookId(id);
      set({ activeBookId: id } as Partial<T>);
    },

    addBook: (book) =>
      set((state) => ({
        items: [...state.items, book],
      } as Partial<T>)),

    updateBook: (id, patch) =>
      set((state) => ({
        items: state.items.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      } as Partial<T>)),

    removeBook: (id) =>
      set((state) => {
        const remaining = state.items.filter(
          (b) => b.id !== id && !b.deleted_at,
        );
        if (remaining.length === 0) {
          throw new LastBookUndeletableError();
        }
        const items = state.items.filter((b) => b.id !== id);
        let activeBookId = state.activeBookId;
        if (activeBookId === id) {
          activeBookId = resolveActiveBookId(items, null);
        }
        return { items, activeBookId } as Partial<T>;
      }),
  });
